import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Wallet, WalletType } from '../../../../types/wallet'
import { Typography } from '@material-ui/core'
import ReactMarkdown from 'react-markdown'

const useStyles = makeStyles((theme) => ({
  root: {
    margin: '2rem 0',
  },
  title: {
    fontSize: '20px',
    fontWeight: 600,
    margin: '1rem 0',
  },
  factsContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: '1fr 1fr',
    gridTemplateAreas:
      ' "in_short in_short" "coins coins" "paymentMethods paymentMethods" "1fr 1fr"',
    columnGap: '1rem',
    rowGap: '1rem',
    '& > div': {},
  },
  fact: {
    margin: '0',
  },
  factTitle: {
    fontSize: '14px',
    color: '#7D7787',
    margin: '.5rem 0',
    fontWeight: 500,
  },
  factValue: {
    fontSize: '14px',
    margin: '.5rem 0',
    fontWeight: 500,
  },
  markdown: {
    fontSize: '.8rem',
    color: '#555555',
    lineHeight: '1.8em',
    overflowWrap: 'break-word',
    '& img': {
      maxWidth: '100%',
    },
    '& a': {
      color: theme.palette.primary.main,
      fontWeight: 'bold',
    },
  },
  reviewMarkdown: {
    color: 'black',
    fontWeight: 500,
  },
  inShort: {
    gridArea: 'in_short',
  },
  coins: {
    gridArea: 'coins',
  },
  paymentMethods: {
    gridArea: 'paymentMethods',
  },
}))

interface props {
  wallet: Wallet
  type: WalletType
}

export default function KeyFacts({ wallet, type }: props): JSX.Element {
  const classes = useStyles({})

  const card_fees_in = wallet?.fees?.europe?.fees['cc']?.buy || ''
  const card_fees_out = wallet?.fees?.europe?.fees['cc']?.sell || ''

  const bank_fees_in = wallet?.fees?.europe?.fees['bank']?.buy || ''
  const bank_fees_out = wallet?.fees?.europe?.fees['bank']?.sell || ''

  return (
    <div className={classes.root}>
      <div>
        <Typography className={classes.title} variant="body2">
          Key Facts
        </Typography>
        <div className={classes.factsContainer}>
          {wallet.in_short && (
            <div className={`${classes.inShort} ${classes.fact}`}>
              <Typography variant="body2" className={classes.factTitle}>
                In short
              </Typography>
              <ReactMarkdown className={`${classes.markdown} ${classes.reviewMarkdown}`}>
                {wallet.in_short}
              </ReactMarkdown>
            </div>
          )}

          {wallet.cryptocurrencies && (
            <div className={`${classes.coins} ${classes.fact}`}>
              <Typography variant="body2" className={classes.factTitle}>
                Coins
              </Typography>
              <Typography variant="body2" className={classes.factValue}>
                {wallet.cryptocurrencies.map((coin) => coin.name).join(', ')}.
              </Typography>
            </div>
          )}

          {wallet.paymentmethods && type === WalletType.custodial && (
            <div className={`${classes.paymentMethods} ${classes.fact}`}>
              <Typography variant="body2" className={classes.factTitle}>
                Payment Methods
              </Typography>
              <Typography variant="body2" className={classes.factValue}>
                {wallet.paymentmethods.map((method) => method.name).join(', ')}.
              </Typography>
            </div>
          )}

          {wallet.networks && type === WalletType.nonCustodial && (
            <div className={` ${classes.fact}`}>
              <Typography variant="body2" className={classes.factTitle}>
                Networks
              </Typography>
              <Typography variant="body2" className={classes.factValue}>
                {wallet.networks}.
              </Typography>
            </div>
          )}

          {wallet.integrations && type === WalletType.physical && (
            <div className={` ${classes.fact}`}>
              <Typography variant="body2" className={classes.factTitle}>
                Integrations
              </Typography>
              <Typography variant="body2" className={classes.factValue}>
                {wallet.integrations}.
              </Typography>
            </div>
          )}

          {wallet.regions && (
            <div className={` ${classes.fact}`}>
              <Typography variant="body2" className={classes.factTitle}>
                Regions
              </Typography>
              <Typography variant="body2" className={classes.factValue}>
                {wallet.regions}.
              </Typography>
            </div>
          )}

          {card_fees_in && card_fees_out && (
            <div className={classes.fact}>
              <Typography variant="body2" className={classes.factTitle}>
                Card - Fees in / Fees out
              </Typography>
              <Typography variant="body2" className={classes.factValue}>
                {card_fees_in} / {card_fees_out}
              </Typography>
            </div>
          )}

          {bank_fees_in && bank_fees_out && (
            <div className={classes.fact}>
              <Typography variant="body2" className={classes.factTitle}>
                Bank - Fees in / Fees out
              </Typography>
              <Typography variant="body2" className={classes.factValue}>
                {bank_fees_in} / {bank_fees_out}
              </Typography>
            </div>
          )}

          {wallet.price && (
            <div className={classes.fact}>
              <Typography variant="body2" className={classes.factTitle}>
                Price
              </Typography>
              <Typography variant="body2" className={classes.factValue}>
                {`$${wallet.price}`}
              </Typography>
            </div>
          )}

          {wallet.release_year && (
            <div className={classes.fact}>
              <Typography variant="body2" className={classes.factTitle}>
                Release Year
              </Typography>
              <Typography variant="body2" className={classes.factValue}>
                {wallet.release_year}
              </Typography>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
